import axios, { Canceler } from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';
import moment, { unitOfTime } from 'moment';
import { branchStore } from '../branchStore';
import { localeStore } from '../localesStore';
import { OrdersHistoryStore } from '../ordersHistoryStore/ordersHistoryStore';
import { CallCenterStore } from '../ordersStore/callCenterStore';
import { LiveOrdersStore, liveOrdersStore } from '../ordersStore/liveOrdersStore';
import { modalStore } from '../modalStore';
import { ORDER_EDIT_STAGE, defaultCalculator, defaultCheckoutDependencies, defaultCheckoutParams } from './constants';
import { ICalculator, ICartCheckout, ICheckoutDependencies, ICheckoutValidatorErrors } from './types';
import { defaultBranch } from 'src/constants/branches';
import { BRANCH_DELIVERY_TYPE, DELIVERY_TYPE, DELIVERY_TYPES, getOrderDeliveryType } from 'src/constants/deliveryTypes';
import { defaultPagination, PAYMENT_TYPES, PER_PAGE } from 'src/constants/main';
import { ORDER_DINE_IN_TYPE, ORDER_STATUS } from 'src/constants/order';
import client from 'src/services/client';
import { IBranch } from 'src/types/branches';
import { IPagination, TBooleanInNumber } from 'src/types/main';
import { PRODUCT_ORDER_TYPE } from 'src/types/product';
import { IMenuProduct } from 'src/types/products';
import { ICustomer } from 'src/types/user';
import { formatPhone, formatPrice, formatPriceSettings } from 'src/utils/formatter';
import {
  booleanToNumber,
  getCancelToken,
  getCurrencySettings,
  getTranslate,
  hasBranch,
  isCashbackErrorCode,
  isOrderPreorder,
} from 'src/utils/helpers';
import { debounce } from 'src/utils/perfomance';
import { emailValidation, phoneValidation } from 'src/utils/validation';
import { dateTimeFormatForRequest } from '~/constants/dateTime';
import { history } from '~/services/history';
import logger from '~/services/logger';
import { TOrder, orderCast } from '~/types/orders';
import { productUtils } from '~/utils/productUtils';
import { toastSuccess } from '~/utils/toasts';
import getOrdersPath from '~/view/screens/CheckoutScreen/getOrdersPath';
import { CashbackErrorCode } from '~/constants/errors';
import { confirmFullRefundAfterDeleteAllProducts } from '~/utils/modalConfirmations';
import { OrdersRatingStore } from '~/view/screens/OrdersRatingScreen/store/ordersRatingStore';
import { IDeliveryZone } from '~/types/deliveryManagment';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import { userStore } from '../userStore';
import { deliveryTypeStore } from '~/view/screens/DeliveryTypeScreen/store/deliveryTypeStore';

export class CheckoutStore {
  constructor(public parentStore: LiveOrdersStore | CallCenterStore | OrdersHistoryStore | OrdersRatingStore) {
    makeObservable(this);
  }

  cancel: {
    [key: string]: null | Canceler;
  } = {
    decompose: null,
    update: null,
    calculate: null,
    create: null,
    reorder: null,
  };

  @observable loading: boolean = false;

  @observable isDraft: boolean = false;

  @observable zonesLastUpdate: number = 0;
  @observable zonesLoading: boolean = false;

  @observable selectedZone: IPickerOption | undefined;
  @observable zonesOptions: IPickerOption[] = [];

  @observable selectedAddress: IPickerOption | undefined;
  @observable addresses: IPickerOption[] = [];

  @observable isZoneAddress = false;

  /**
   * Stage(tab) of working with an order.
   *
   * @type {ORDER_EDIT_STAGE}
   * @memberof CheckoutStore
   */
  @observable orderEditStage: ORDER_EDIT_STAGE = ORDER_EDIT_STAGE.DETAILS;
  @action setOrderEditStage = (stage: ORDER_EDIT_STAGE) => {
    this.orderEditStage = stage;

    if (stage === ORDER_EDIT_STAGE.DETAILS && this.parentStore instanceof CallCenterStore) {
      this.parentStore.showOrderList();
    } else {
      if (this.parentStore instanceof CallCenterStore) {
        this.parentStore.hideOrderList();
      }
      // recalculate, if tab include cart
      if (stage === ORDER_EDIT_STAGE.CHECKOUT) {
        this.cashbackError = null;
      }
      this.calculate();
    }
  };

  @observable validatorErrors: ICheckoutValidatorErrors = {};
  @observable dependencies: ICheckoutDependencies = defaultCheckoutDependencies;
  @observable calculator: ICalculator = defaultCalculator;
  @observable params: ICartCheckout = defaultCheckoutParams();

  @observable shouldCatchCalculationErrors: boolean = false;
  @observable cashbackError: CashbackErrorCode | null = null;

  @action setCalcErrorsCatching = (status: boolean) => {
    this.shouldCatchCalculationErrors = status;
  };

  @computed get supportedProductOrderType(): PRODUCT_ORDER_TYPE {
    if (this.params.delivery_type === DELIVERY_TYPE.DELIVERY) {
      return PRODUCT_ORDER_TYPE.DELIVERY;
    }
    if (this.params.is_dinein) {
      return PRODUCT_ORDER_TYPE.DINEIN;
    }

    return PRODUCT_ORDER_TYPE.PICKUP_CURBSIDE;
  }

  @computed get availablePaymentTypes(): PAYMENT_TYPES[] {
    const { branch } = this.dependencies;

    const defaultPaymentTypes = [PAYMENT_TYPES.CASH, PAYMENT_TYPES.ONLINE_PAYMENT, PAYMENT_TYPES.TERMINAL];

    if (!branch) {
      return defaultPaymentTypes;
    }

    if (branch.allow_all_payment_methods) {
      return branch.available_payment_types.map((paymentType) => paymentType.id);
    }

    if (this.params.is_dinein && branch.emenu?.payment_types.length) {
      return branch.emenu.payment_types.map((type) => type.id);
    }

    return branch.payment_types.map((type) => type.id);
  }

  @action private _disableCashbackErrors = () => {
    this.dependencies.cashback_warnings_as_errors = 0;
  };

  //   CUSTOMER INFO
  @action selectCustomer = (customer: ICustomer | undefined) => {
    this.dependencies.customer = customer;
    if (!customer) {
      this.toggleUseWallet(0);
    }
    this.onOrderChanged();
  };
  @action changeUserInfo = (field: keyof ICheckoutDependencies['user'], value: string) => {
    this.dependencies.user[field] = value;
    this.onOrderChanged();
  };

  // PREORDER
  @action changePreorder = (preorder: ICartCheckout['preorder']) => {
    this.params.preorder = preorder;
    this.handleDueTime();
    this.onOrderChanged();
  };
  private handleDueTime = () => {
    // if !ASAP (ASAP = 0), then return
    if (this.params.preorder) return;

    const preorderDate = moment();
    const { time, unit } = this.preparationTime;
    preorderDate.add(time, unit as unitOfTime.DurationConstructor);

    this.changePreorderDate(preorderDate.toDate());
  };

  private get preparationTime() {
    let time: number | undefined = 0;
    const unit: string = 'm';

    const currentBranch: IBranch | undefined = branchStore.branches.find((branch) => branch.id === this.dependencies.branch?.id);
    if (!currentBranch) {
      return { time, unit };
    }

    // Currently preparation_time always in minutes, when it's fixed - use commented
    if (this.params.delivery_type === DELIVERY_TYPES.DELIVERY) {
      time = currentBranch.preparation?.delivery.preparation_time;
      // unit = currentBranch.preparation?.delivery.unit;
    } else if (this.params.delivery_type === DELIVERY_TYPES.PICKUP) {
      time = currentBranch.preparation?.pickup.preparation_time;
      // unit = currentBranch.preparation?.pickup.unit;
    }

    return { time, unit };
  }
  @action changePreorderDate = (preorder_date: ICartCheckout['preorder_date']) => {
    if (!moment(this.params.preorder_date).isSame(preorder_date)) {
      this.params.preorder_date = preorder_date;
      this.onOrderChanged();
    }
  };
  //   CURBSIDE
  @action toggleCurbside = (is_curbside: TBooleanInNumber, skipUpdate?: boolean) => {
    this.params.is_curbside = is_curbside;
    if (!skipUpdate) {
      this.onOrderChanged();
    }
  };
  @action changeCurbsideInfo = (field: keyof ICheckoutDependencies['curbside'], value: string) => {
    this.dependencies.curbside[field] = value;
    this.onOrderChanged();
  };
  //   DINE IN
  @action toggleDineIn = (is_dinein: TBooleanInNumber, skipUpdate?: boolean) => {
    this.params.is_dinein = is_dinein;
    if (!skipUpdate) {
      this.onOrderChanged();
    }
  };
  @action changeDineInInfo = (field: keyof ICheckoutDependencies['dinein'], value: any) => {
    if (field === 'section') {
      this.dependencies.dinein.table = {
        id: 0,
        name: '',
      };
    }
    this.dependencies.dinein[field] = value;
    this.onOrderChanged();
  };
  @action defineDinein = async (branch: IBranch) => {
    try {
      this.dependencies.dinein.section = {
        id: 0,
        name: '',
      };

      this.dependencies.dinein.table = {
        id: 0,
        name: '',
      };

      this.dependencies.dinein.dinein_delivery_type =
        this.dependencies.emenu.data?.order_types.is_dinein || !this.dependencies.emenu.data?.order_types
          ? ORDER_DINE_IN_TYPE.DINE_IN
          : ORDER_DINE_IN_TYPE.TOGO;

      await this.fetchBranchEmenu(branch.id);
    } catch (error: any) {
      logger.error({ error, place: 'CheckoutStore defineDinein' });
    }
  };

  @action fetchBranchEmenu = async (id: IBranch['id']) => {
    try {
      this.dependencies.emenu.loading = true;
      const branchEmenu = await client.get(`/v4/branches/${id}/emenu`);
      this.dependencies.emenu.data = branchEmenu;
      this.dependencies.emenu.loading = false;
    } catch (error: any) {
      this.dependencies.emenu.loading = false;
      logger.error({ error, place: 'CheckoutStore fetchBranchEmenu' });
    }
  };
  //   WALLET
  @action toggleUseWallet = (use_wallet: TBooleanInNumber) => {
    this.params.use_wallet = use_wallet;
    this.onOrderChanged();
  };
  //   PROMOCODE
  @action changePromocode = (promocode: string) => {
    this.params.promocode = promocode;
    this.onOrderChanged();
  };

  //   NOTES
  @action changeNotes = (notes: string) => {
    this.params.notes = notes;
  };

  //   DELIVERY TYPE
  @action changeDeliveryType = (delivery_type: ICartCheckout['delivery_type']) => {
    this.params.delivery_type = delivery_type;

    if (this.orderEditStage === ORDER_EDIT_STAGE.CHECKOUT) {
      this.setCalcErrorsCatching(true);
    }

    this.handleDueTime();
    this.onOrderChanged();
  };

  //   PAYMENT INFO
  @action changePaymentType = (payment_type: ICartCheckout['payment_type']) => {
    this.params.payment_type = payment_type;
    this.onOrderChanged();
  };

  //   ORDER FOR SOMEONE ELSE
  @action toggleOrderForSomeoneElse = (is_order_for_someone_else: TBooleanInNumber) => {
    this.params.is_order_for_someone_else = is_order_for_someone_else;
    this.onOrderChanged();
  };
  // toggle send sms in checkout
  @action toggleSendSMS = (toggle_send_sms_with_geo: TBooleanInNumber) => {
    this.params.send_sms_with_geo = toggle_send_sms_with_geo;
  };
  @action changeStrangerInfo = (field: keyof ICheckoutDependencies['stranger'], value: string) => {
    this.dependencies.stranger[field] = value;
    this.onOrderChanged();
  };

  // SELECT BRANCH
  @action selectBranch = (branch: IBranch) => {
    this.dependencies.previous_branch_id = this.dependencies.branch.id;
    this.dependencies.branch = branch;

    const preorderDate = moment()
      .add(branch.preparation?.delivery.preparation_time, branch.preparation?.delivery.unit as unitOfTime.DurationConstructor)
      .toDate();
    this.changePreorderDate(preorderDate);

    if (!branch.preorder) this.changePreorder(0);
    // payment type
    this.params.payment_type = branch.default_payment_type;
    // delivery type
    if (!this.params.delivery_type || (branch.delivery_type !== BRANCH_DELIVERY_TYPE.BOTH && branch.delivery_type !== this.params.delivery_type)) {
      this.params.delivery_type = branch.delivery_type === DELIVERY_TYPES.DELIVERY ? BRANCH_DELIVERY_TYPE.DELIVERY : BRANCH_DELIVERY_TYPE.PICKUP;
    }
    if (this.params.delivery_type === DELIVERY_TYPE.DELIVERY) {
      if (!this.dependencies.location?.address_id && !this.dependencies.location?.lat) {
        this.dependencies.location = {
          address: this.dependencies.branch.address,
          lat: this.dependencies.branch.latitude,
          lng: this.dependencies.branch.longitude,
        };
      }
    }
    // curbside
    if (!branch.is_curbside) {
      if (!branch.preorder) this.toggleCurbside(0);
    }
    this.onOrderChanged();
  };
  @action selectLocation = (location: ICheckoutDependencies['location']) => {
    this.dependencies.location = location;
    this.onOrderChanged();
  };

  // PRODUCTS
  @action addProduct = (_product: IMenuProduct) => {
    const product = {
      ..._product,
      orderable: true,
      hash: productUtils.getProductHash(_product),
    };

    const sameProductIdx = this.dependencies.products.findIndex((__product) => __product.hash === product.hash);

    if (~sameProductIdx) {
      this.dependencies.products[sameProductIdx].quantity = this.dependencies.products[sameProductIdx].quantity + product.quantity;
    } else {
      this.dependencies.products = [...this.dependencies.products, product];
    }

    this.onOrderChanged({ skipCheckTotal: true });
  };
  @action updateProduct = (product: IMenuProduct) => {
    let updated = false;

    this.dependencies.products = this.dependencies.products.map((_product) => {
      if (_product.hash === product.hash) {
        updated = true;
        product.orderable = true;
        // update hash
        product.hash = productUtils.getProductHash(product);

        return product;
      }

      return _product;
    });

    if (!updated) this.addProduct(product);
    this.onOrderChanged({ skipCheckTotal: true });
  };

  @action onRemoveLastProduct = (product: IMenuProduct) => {
    confirmFullRefundAfterDeleteAllProducts({
      message: localeStore.t('cc_emptycart_dbx_bodytext'),
      onConfirm: () => (
        (this.dependencies.products = this.dependencies.products.filter((_product) => _product.hash !== product.hash)),
        this.onOrderChanged({ skipCheckTotal: true })
      ),
    });
  };

  @action onRemoveProduct = (product: IMenuProduct) => {
    const isOrderCompleted = this.dependencies.order?.order_status_code === ORDER_STATUS.COMPLETED;
    const productLengthLessOne = this.dependencies.products.length <= 1;

    if (productLengthLessOne && isOrderCompleted) {
      this.onRemoveLastProduct(product);
    } else {
      this.dependencies.products = this.dependencies.products.filter((_product) => _product.hash !== product.hash);
      this.onOrderChanged({ skipCheckTotal: true });
    }
  };

  @action public changeStatus = async (id: TOrder['id'], status: TOrder['order_status_code'], refund_message?: string) => {
    try {
      if (!this.dependencies.order) return false;
      this.loading = true;

      const response = await client.patch(`/v3/orders/${id}`, { status, refund_message });
      const order = orderCast(response);
      if (this.dependencies.order?.id === order.id) this.dependencies.order = order;

      if (this.parentStore instanceof CallCenterStore || this.parentStore instanceof LiveOrdersStore) {
        this.parentStore.handleUpdateOrder(order);
      }

      this.loading = false;

      if (status === ORDER_STATUS.CANCELED) {
        toastSuccess({
          title: localeStore.t('toast_callcenter_order_cancelled'),
          message: `#${String(response.order_key)} - ${localeStore.t(String(response.order_status_code) + '_status')}`,
        });
      }

      return true;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'CheckoutStore changeStatus', showModal: true });
      return false;
    }
  };

  // REFUND REASON
  @action addRefundReason = (refund_reason: ICheckoutDependencies['refund_reason'] | undefined) => {
    this.dependencies.refund_reason = refund_reason;
  };

  // calculate
  @action calculate = async (params = { keepCashbackError: false, skipCheckTotal: false }) => {
    const { keepCashbackError, skipCheckTotal } = params;
    try {
      if (this.cancel.calculate) this.cancel.calculate('Cancelled');
      // this.validatorErrors = this.validator();
      this.calculator.loading = true;
      // await delay(3000);
      const data: any = {
        customer_id: this.dependencies.customer?.id,
        use_wallet: this.params.use_wallet,
        delivery_type: this.params.delivery_type,
        // DO NOT PUT PROMOCODE IF PAYMENT TYPE  WALLET IS ACTIVE
        promocode: this.params.use_wallet ? '' : this.params.promocode,
        payment_type: this.params.payment_type,
        branch_id: this.dependencies.branch.id ? this.dependencies.branch.id : undefined,
        products: this.dependencies.products.map(productUtils.transformProductForCalculator),
        is_curbside: this.params.is_curbside,
      };

      if (this.dependencies.products.length) {
        data.return_items = true;
      }

      if (!this.isDraft) {
        data.order_id = this.dependencies.order?.id;
      }

      if (this.params.preorder && this.params.preorder_date) {
        // send unix in seconds
        data.time = moment(this.params.preorder_date).unix();
      }

      if (this.params.delivery_type === DELIVERY_TYPE.DELIVERY) {
        data.address_id = this.dependencies.location?.address_id;

        // not sent coords if address already specified to prevent creation of duplications
        if (!data.address_id) {
          delete data.address_id;
          data.latitude = this.dependencies.location?.lat;
          data.longitude = this.dependencies.location?.lng;
        }
      }

      if (this.params.is_dinein && this.dependencies.dinein.dinein_delivery_type === ORDER_DINE_IN_TYPE.DINE_IN) {
        data.delivery_type = DELIVERY_TYPE.DINE_IN;
        data.dinein_delivery_type = this.dependencies.dinein.dinein_delivery_type;
      }

      if (this.calculator.cashback?.id) {
        data.previous_calculation = {
          cashback_id: this.calculator.cashback.id,
          cashback_amount: this.calculator.cashback.amount,
          branch_id: this.dependencies.previous_branch_id || data.branch_id,
          order_type: this.dependencies.previous_order_type || data.delivery_type,
        };
      }

      this.dependencies.previous_order_type = data.delivery_type;
      this.dependencies.previous_branch_id = data.branch_id;

      const response = await client.post('/v3/orders/calculate', data, {
        params: { is_draft: booleanToNumber(this.isDraft) },
        cancelToken: getCancelToken((c) => (this.cancel.calculate = c)),
      });

      let needToRecalculate = false;
      if (
        !skipCheckTotal &&
        response.total &&
        this.calculator.total &&
        response.total !== this.calculator.total &&
        this.orderEditStage === ORDER_EDIT_STAGE.CHECKOUT
      ) {
        this.orderEditStage = ORDER_EDIT_STAGE.PRODUCTS;
        needToRecalculate = true;
      }

      if (isCashbackErrorCode(response?.cashback?.warning_code) && this.orderEditStage === ORDER_EDIT_STAGE.CHECKOUT) {
        // If cashback just appeared or amount is not changed - don't show ChangedConditions warning
        this.cashbackError =
          ((!this.calculator.cashback?.id && response?.cashback?.id) ||
            (this.calculator.cashback?.id && this.calculator.cashback.amount === response.cashback.amount)) &&
          response.cashback.warning_code === CashbackErrorCode.ChangedConditions
            ? null
            : (response.cashback.warning_code as CashbackErrorCode);
        // If previous error was Unavailable - user can clear by himself
      } else if (
        this.cashbackChanged &&
        // If calculate after createOrder error - keep error visible
        !keepCashbackError
      ) {
        this.cashbackError = null;
      }

      if (response.products.length) {
        const updatedProducts = productUtils.transformProductsFromCalculator(this.dependencies.products, response.products);

        this.dependencies.products = updatedProducts;
        this.calculator = { ...response };
      } else {
        this.calculator = { ...response, cashback: defaultCalculator.cashback };
      }

      if (needToRecalculate) {
        this.calculate();
      }

      this.calculator.loading = false;
      this.validate();
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.calculator = defaultCalculator;
      this.calculator.loading = false;

      logger.error({ error, place: 'CheckoutStore calculate' });
    }
  };

  decompose = async (order_id: TOrder['id']) => {
    try {
      if (this.cancel.decompose) this.cancel.decompose('Cancelled');

      const response = await client.get(`/v3/orders/draft`, {
        params: { order_id },
        cancelToken: getCancelToken((c) => (this.cancel.decompose = c)),
      });

      const order: TOrder = response.order;

      // if ([ORDER_STATUS.CANCELED, ORDER_STATUS.WAITING_FOR_PAYMENT, ORDER_STATUS.ON_HOLD].includes(order.order_status_code)) {
      //   // history.push(getOrderScreenPath(order.id));
      //   return;
      // }

      this.isDraft = order.order_status_code === ORDER_STATUS.DRAFT;
      this.dependencies.user.phone = order.customer_mobile;
      this.dependencies.user.name = order.customer_name;
      this.dependencies.user.email = order.customer_email;

      this.isZoneAddress = order.is_zone_address;
      if (this.isZoneAddress) {
        this.selectedZone = { label: getTranslate(order.zone_name), value: order.area_id };
        this.selectedAddress = { label: localeStore.t('call_center_fake_address_placeholder'), value: null };
      } else if (order.address_id) {
        this.selectedAddress = { label: order.address, value: order.address_id };
      }

      if (order.customer) {
        this.selectCustomer(order.customer);
      }

      if (response.area) {
        this.dependencies.location = {
          area_id: response.area.id,
          address: response.address,
          address_id: response.address_id,
          lat: response.area.latitude,
          lng: response.area.longitude,
        };
      }

      if (order.is_order_for_someone_else && order.stranger_customer_info) {
        this.params.is_order_for_someone_else = booleanToNumber(order?.is_order_for_someone_else);
        this.dependencies.stranger.name = order.stranger_customer_info.name;
        this.dependencies.stranger.phone = order.stranger_customer_info.mobile;
        this.dependencies.stranger.id = order.stranger_customer_info.stranger_id;
      }
      if (order.is_curbside) {
        this.params.is_curbside = booleanToNumber(order.is_curbside);
        this.dependencies.curbside = {
          car_brand: order.curbside?.car_brand || '',
          car_color: order.curbside?.car_color || '',
          car_type: order.curbside?.car_type || '',
          car_number: order.curbside?.car_number || '',
        };
      }
      // dinein in order type and dinein in response order are different
      const isDineIn: boolean = Boolean(order?.dinein?.dinein_delivery_type === ORDER_DINE_IN_TYPE.DINE_IN);

      if (isDineIn) {
        this.dependencies.dinein.dinein_delivery_type = response.order.dinein.dinein_delivery_type;
        this.dependencies.dinein.section = {
          id: response.order.dinein.section_id,
          name: response.order.section,
        };
        this.dependencies.dinein.table = {
          id: response.order.dinein.table_id,
          name: response.order.table,
        };
        this.toggleDineIn(1);
      }

      response.products?.map((product: IMenuProduct) => this.addProduct(productUtils.transformProduct(product)));

      this.params.use_wallet = order.use_wallet || 0;
      this.params.promocode = order.promocode || '';
      this.params.delivery_type = getOrderDeliveryType(order.delivery_type_code) || DELIVERY_TYPES.PICKUP;
      this.params.notes = order.notes || '';

      // payment type
      let paymentType: PAYMENT_TYPES = PAYMENT_TYPES.CASH;
      if (order.payment_type || order.payment_type === PAYMENT_TYPES.CASH) {
        const isAvailableCurrentType: boolean = isDineIn
          ? response.branch?.emenu?.payment_types?.some((type: { id: PAYMENT_TYPES }) => type.id === order.payment_type)
          : response.branch?.payment_types?.some((type: { id: PAYMENT_TYPES }) => type.id === order.payment_type);

        // if not a draft, then we apply the selected type, regardless of its availability
        const isSelectCurrentType: boolean = !this.isDraft || response.branch?.allow_all_payment_methods || isAvailableCurrentType;

        if (isSelectCurrentType) {
          paymentType = order.payment_type;
        } else if (isDineIn && response.branch?.emenu?.payment_types?.length) {
          paymentType = response.branch.emenu.payment_types[0].id;
        } else if (response.branch?.payment_types?.length) {
          paymentType = response.branch.payment_types[0].id;
        }
      }

      this.params.payment_type = paymentType;
      //

      if (response.branch) {
        this.dependencies.branch = response.branch;

        if (response.branch.emenu?.order_types.is_dinein) {
          this.dependencies.branch.is_emenu = true;
          this.dependencies.emenu.data = response.branch.emenu;
        }
      } else {
        this.dependencies.branch = defaultBranch;
      }

      this.handleDueTime();
      if (isOrderPreorder(order.delivery_type_code)) {
        this.params.preorder = 1;
        this.params.preorder_date = moment(order.order_datetime, dateTimeFormatForRequest).toDate();
      }

      this.dependencies.order = order;

      // if it’s a draft, then we go straight to products selection stage
      this.setOrderEditStage(this.isDraft ? ORDER_EDIT_STAGE.DETAILS : ORDER_EDIT_STAGE.PRODUCTS);
    } catch (error: any) {
      if (error.message === 'Cancelled') return;

      logger.error({
        error,
        place: 'CheckoutStore decompose',
        showModal: true,
        modalProps: {
          id: 'order_view_error',
          onClose: () => {
            history.push(getOrdersPath(this.parentStore));
          },
        },
      });
    }
  };

  discard = async () => {
    try {
      if (!this.dependencies.order?.id) return;
      this.loading = true;
      if (this.parentStore instanceof CallCenterStore) {
        await this.parentStore.deleteDraft(String(this.dependencies.order?.id));
      }
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'CheckoutStore discard' });
    }
  };

  createOrder = async () => {
    try {
      if (this.cancel.create) this.cancel.create('Cancelled');
      if (!this.dependencies.order?.id) return false;

      this.loading = true;

      liveOrdersStore.ignoreOrderForSound(String(this.dependencies.order.id));

      const response: ICheckoutDependencies['order'] = await client.post(
        '/v3/orders',
        {
          ...this.data,
          order_id: this.dependencies.order?.id,
          cashback_warnings_as_errors: this.dependencies.cashback_warnings_as_errors,
        },
        { cancelToken: getCancelToken((c) => (this.cancel.create = c)) }
      );

      // this.dependencies.order = { ...this.dependencies.order, ...response };

      toastSuccess({
        title: localeStore.t('toast_order_placed'),
        message: `#${String(response?.order_key)} - ${localeStore.t(String(response?.order_status_code) + '_status')}`,
      });

      if (this.parentStore instanceof CallCenterStore) {
        this.parentStore.handleUpdateOrder(response);
      }

      this.loading = false;
      return true;
    } catch (error: any) {
      if (error.message === 'Cancelled') return false;
      this.loading = false;

      if (isCashbackErrorCode(error?.response?.data?.code)) {
        this.cashbackError = (error.response.data.code as CashbackErrorCode) || null;
        this.calculate({ keepCashbackError: true, skipCheckTotal: false });
      } else {
        logger.error({ error, place: 'CheckoutStore createOrder', showModal: true });
      }

      return false;
    }
  };

  /**
   *
   * @param {boolean} [click] - indicates that function call was made by a user click
   * @returns
   */
  update = async (click?: boolean) => {
    try {
      if (this.cancel.update) this.cancel.update('Cancelled');
      if (!this.dependencies.order?.id) return false;
      this.loading = true;
      const [data] = this.validator();

      const response: TOrder = await client.put(`/v3/orders/${this.dependencies.order?.id}`, data, {
        params: { is_draft: booleanToNumber(this.isDraft), cancelToken: getCancelToken((c) => (this.cancel.update = c)) },
      });

      this.dependencies.order = { ...this.dependencies.order, ...response };

      // set relevant address_id
      if (this.dependencies.location) this.dependencies.location.address_id = response.address_id;

      if (!this.isDraft) {
        toastSuccess({
          title: localeStore.t('toast_order_updated'),
          message: `#${String(response.order_key)} - ${localeStore.t(String(response.order_status_code) + '_status')}`,
        });
      }

      this.loading = false;
      return true;
    } catch (error: any) {
      if (error.message === 'Cancelled') return false;
      this.loading = false;

      // @ts-ignore
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        // @ts-ignore
        modalStore.openErrorModal({ message: error.response.data.message });
      }

      if (this.orderEditStage === ORDER_EDIT_STAGE.CHECKOUT && click) {
        logger.error({ error, place: 'CheckoutStore update', showModal: true });
      }

      return false;
    }
  };

  @action reorder = async (order: TOrder | undefined) => {
    try {
      if (!order) return;
      if (this.cancel.reorder) this.cancel.reorder('Cancelled');
      this.loading = true;

      const reorder = await client.get(`/v3/orders/draft`, {
        params: { order_id: order.id, cancelToken: getCancelToken((c) => (this.cancel.update = c)) },
      });

      this.dependencies.products = [];
      if (reorder.products.length) {
        reorder.products.map((product: IMenuProduct) => this.addProduct(productUtils.transformProduct(product)));
      }

      this.loading = false;
      this.onOrderChanged();
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'CheckoutStore reorder', showModal: true });
    }
  };

  get data() {
    const data: any = {
      delivery_type: this.params.delivery_type,
      use_wallet: this.params.use_wallet,

      payment_type: this.params.payment_type,
      branch_id: this.dependencies.branch.id ? this.dependencies.branch.id : undefined,
      area_id: this.selectedZone?.value ? this.selectedZone?.value : this.dependencies.location?.area_id,
      user_id: this.dependencies.customer?.id,
      language: localeStore.language,
      subtotal: this.calculator.promocode ? this.calculator.discounted_subtotal : this.calculator.subtotal,
      total: this.calculator.promocode ? this.calculator.discounted_total : this.calculator.total,
      vat: this.calculator.vat,
      send_sms: this.params.send_sms_with_geo,

      is_order_for_someone_else: this.params.is_order_for_someone_else,

      delivery_fee: this.calculator.delivery_fee,
      is_curbside: this.params.is_curbside,
      curbside: this.params.is_curbside ? this.dependencies.curbside : undefined,
      notes: this.params.notes,
      products: this.dependencies.products.filter((product) => product.orderable).map(productUtils.transformProductForCalculator),

      customer_id: this.dependencies.customer?.id,
      customer_first_name: this.dependencies.user.name,
      customer_mobile: formatPhone(this.dependencies.user.phone),
      customer_email: this.dependencies.user.email,

      is_zone_address: !!this.selectedZone?.value,
    };

    if (this.params.is_dinein && this.dependencies.dinein.dinein_delivery_type === ORDER_DINE_IN_TYPE.DINE_IN) {
      data.delivery_type = DELIVERY_TYPE.DINE_IN;
      data.dinein_delivery_type = this.dependencies.dinein.dinein_delivery_type;
      data.section = this.dependencies.dinein.section?.id;
      data.table = this.dependencies.dinein.table?.id;
    }

    if (this.calculator.cashback?.id) {
      data.previous_calculation = {
        cashback_id: this.calculator.cashback.id,
        cashback_amount: this.calculator.cashback.amount,
        branch_id: this.dependencies.previous_branch_id || this.dependencies.branch.id,
        order_type: this.dependencies.previous_order_type || data.delivery_type,
      };
    }

    // if order for someone else
    if (this.params.is_order_for_someone_else) {
      data.should_contact_by_driver = false;
      data.stranger_mobile = formatPhone(this.dependencies.stranger.phone);
      if (this.dependencies.stranger.name) {
        data.stranger_recipient_name = this.dependencies.stranger.name;
      }
    }

    if (this.params.delivery_type === DELIVERY_TYPE.DELIVERY) {
      data.address_id = this.dependencies.location?.address_id;

      // not sent coords if address already specified to prevent creation of duplications
      if (!data.address_id) {
        delete data.address_id;
        data.latitude = this.dependencies.location?.lat;
        data.longitude = this.dependencies.location?.lng;
      }

      if (!data.address_id && !data.latitude && !data.longitude) {
        data.latitude = this.dependencies.branch.longitude;
        data.longitude = this.dependencies.branch.latitude;
      }
    }

    if (!this.isDraft) {
      data.order_id = this.dependencies.order?.id;
      if (this.params.payment_type === PAYMENT_TYPES.ONLINE_PAYMENT) {
        data.reason = this.dependencies.refund_reason;
      }
    }
    // Do not put order date if time not was changed
    if (!this.isDraft && moment(this.dependencies.order?.order_datetime).diff(this.params.preorder_date, 'minutes') > 1) {
      delete data.preorder_date;
    }
    // if preorder exist
    if (this.params.preorder) {
      // send unix in seconds
      data.time = moment(this.params.preorder_date).unix();
    }
    // DO NOT PUT PROMOCODE IF PAYMENT TYPE WALLET IS ACTIVE
    if (this.calculator.promocode || this.params.use_wallet) {
      data.promocode = this.calculator.promocode;
    }
    return data;
  }
  private validator = () => {
    const data = this.data;
    const errors: ICheckoutValidatorErrors = {};

    // CUSTOMER VALIDATION
    if (!phoneValidation(this.dependencies.user.phone)) {
      errors.customer_phone = true;
      delete data.customer_mobile;
      delete data.customer_id;
    }
    if (!this.dependencies.user.name) {
      errors.customer_name = true;
      delete data.customer_first_name;
    }
    if (this.dependencies.user.email && !emailValidation(this.dependencies.user.email).isValid) {
      errors.customer_email = true;
      delete data.customer_email;
    }

    // BRANCH VALIDATION
    if (!this.dependencies.branch.id || !hasBranch(this.dependencies.branch.id)) {
      errors.branch = true;
      delete data.branch_id;
      delete data.address_id;
      delete data.area_id;
    }

    if (this.dependencies.branch.id) {
      // selected delivery type is available in the branch
      if (this.dependencies.branch.delivery_type !== DELIVERY_TYPES.BOTH && this.dependencies.branch.delivery_type !== this.params.delivery_type) {
        errors.delivery_type = true;
        delete data.delivery_type;
      }

      // selected payment type is available in the branch
      if (!this.availablePaymentTypes.some((type) => type === this.params.payment_type) && this.isDraft) {
        errors.payment_type = true;
        delete data.payment_type;
      }
    }

    if (
      this.params.delivery_type !== this.dependencies.branch.delivery_type &&
      this.dependencies.branch.delivery_type !== BRANCH_DELIVERY_TYPE.BOTH
    ) {
      errors.delivery_type = true;
      delete data.delivery_type;
    }

    if (!this.dependencies.branch.preorder && this.params.preorder) {
      errors.preorder = true;
      errors.preorder_date = true;
      delete data.time;
    }
    if (this.params.preorder) {
      if (moment(this.params.preorder_date).isBefore(moment())) {
        errors.preorder_date = true;
        delete data.time;
      }
    }

    // ORDER FOR SM ELSE VALIDATION
    if (this.params.is_order_for_someone_else) {
      // if (this.dependencies.stranger.name) errors['stranger_name'] = true;
      if (!phoneValidation(this.dependencies.stranger.phone)) {
        errors.stranger_phone = true;
        delete data.stranger_mobile;
      }
    }

    // CURBSIDE VALIDATION
    if (this.params.is_curbside) {
      if (!this.dependencies.curbside.car_brand) {
        errors.car_brand = true;
        delete data.curbside.car_brand;
      }
      if (!this.dependencies.curbside.car_color) {
        errors.car_color = true;
        delete data.curbside.car_color;
      }
      if (!this.dependencies.curbside.car_number) {
        errors.car_number = true;
        delete data.curbside.car_number;
      }
      if (!this.dependencies.curbside.car_type) {
        errors.car_type = true;
        delete data.curbside.car_type;
      }
    }

    // DINE IN VALIDATION
    if (this.params.is_dinein) {
      if (!this.dependencies.dinein.section?.id) {
        errors.section = true;
        delete data.section;
      }
      if (!this.dependencies.dinein.table?.id) {
        errors.table = true;
        delete data.table;
      }
    }

    if (Object.keys(errors).length) errors[ORDER_EDIT_STAGE.DETAILS] = true;

    // PRODUCTS VALIDATION
    const isInvalidCart = this.dependencies.products.some((product) => product.error);

    if (!this.dependencies.products.length || isInvalidCart) {
      errors.products = true;
      errors[ORDER_EDIT_STAGE.PRODUCTS] = true;
      delete data.products;
    }
    if (!this.isDraft) {
      if (this.onlinePaymentNewTotalMaxValid()) {
        errors.products = true;
        errors[ORDER_EDIT_STAGE.PRODUCTS] = true;
        delete data.products;
      }
      // prevent from wallet refund
      if (
        this.dependencies.order?.use_wallet &&
        (this.calculator.total < this.dependencies.order.total ||
          (this.dependencies.order.wallet_discount && this.calculator.wallet_discount < this.dependencies.order.wallet_discount))
      ) {
        errors.products = true;
        errors[ORDER_EDIT_STAGE.PRODUCTS] = true;
        delete data.products;
      }
    }

    this.validatorErrors = errors;
    return [data, errors];
  };

  /**
   * Price formatting depending on the branch
   *
   * @param {(string | number)} price
   * @memberof Checkout
   */
  formatPrice = (price: string | number): string => {
    if (this.dependencies.branch) {
      const currency_settings = getCurrencySettings(this.dependencies.branch);
      return formatPriceSettings(price, currency_settings);
    }
    return formatPrice(price);
  };

  onOrderChanged = (params = { skipCheckTotal: false }) => {
    if (this.isDraft) {
      this.validate();
      this.recalculate(params);
      this.reUpdate();
    } else {
      this.validate();
      this.recalculate(params);
    }
  };
  public validate = debounce(this.validator, 200);
  public recalculate = debounce(this.calculate, 200);
  public reUpdate = debounce(this.update, 1000);

  // CANCEL ALL
  @action terminate = () => {
    Object.values(this.cancel).map((token) => token && token('Cancelled'));
  };

  @action prepareAddresses = async () => {
    try {
      if (this.dependencies.customer?.id) {
        const response = await client.get(`v3/users/${this.dependencies.customer?.id}`);

        if (response?.addresses?.length) {
          this.addresses = response?.addresses
            ?.filter((item: { address: string; is_delete: number }) => !!item.address && !item.is_delete)
            .map((item: { address: string; id: number; latitude: number; longitude: number }) => ({
              label: item.address,
              value: item.id,
              data: { lat: item.latitude, lng: item.longitude, area_id: undefined, address: item.address, address_id: item.id },
            }));
        }
      }
    } catch (error) {
      logger.error({ error, place: 'CheckoutStore prepareAddresses' });
    }
  };

  @action selectAddress = async (option: IPickerOption) => {
    const addressData = option.data;
    const addressInfo = await deliveryTypeStore.checkAddress(addressData?.lat, addressData?.lng);

    if (!addressInfo.branch) {
      modalStore.openErrorModal({ message: localeStore.t('cc_err_delivery_is_unavailable') });
      return;
    }

    if (addressInfo.branch?.id && !hasBranch(addressInfo.branch?.id)) {
      modalStore.openErrorModal({ message: localeStore.t('branch_access_error') });
      return;
    }

    this.selectedAddress = option;
    this.selectedZone = undefined;

    if (addressData?.lat && addressData?.lng) {
      this.selectBranch(addressInfo.branch || defaultBranch);
      this.selectLocation(addressData);
    }
  };

  @action prepareZones = async (): Promise<void> => {
    try {
      if (this.zonesLastUpdate && this.zonesOptions.length && moment().diff(moment(this.zonesLastUpdate), 'minutes') < 10) {
        return;
      }

      this.zonesLoading = true;

      let zonesData: IDeliveryZone[] = [];

      const firstPageResponse = await this.fetchPage(1);
      if (!firstPageResponse) return;

      zonesData = firstPageResponse.data;
      const lastPage = firstPageResponse.last_page || 1;

      if (lastPage > 1) {
        const pageRequests = Array.from({ length: lastPage - 1 }, (_, i) => this.fetchPage(i + 2));
        const responses = await Promise.all(pageRequests);

        const validResponses = responses.filter((res) => res !== null && res !== undefined);

        zonesData = [...zonesData, ...validResponses.flatMap((res) => res!.data)].sort((a, b) => a.priority - b.priority);
      }

      this.zonesOptions = zonesData
        .filter((item) => !item.branch?.is_delete && item.branch?.status !== 0 && item.status !== 0)
        .map((item) => ({
          value: item.id,
          label: getTranslate(item.name),
          data: item,
        }));

      this.zonesLastUpdate = Date.now();
    } catch (error: any) {
      logger.error({ error, place: 'CheckoutStore prepareZones' });
    } finally {
      this.zonesLoading = false;
    }
  };

  @action private fetchPage = async (page: number): Promise<IPagination<IDeliveryZone> | null> => {
    try {
      return await client.get('/v3/areas', {
        params: { page, per_page: PER_PAGE.LARGE, branches: userStore.user.branches.map((branch) => branch.id) },
      });
    } catch (error: any) {
      logger.error({ error, place: `CheckoutStore fetchPage - page ${page}` });
      return null;
    }
  };

  @action selectZone = async (option: IPickerOption) => {
    const zoneAddressInfo = await deliveryTypeStore.checkAddress(option.data?.centre_polygon.latitude, option.data?.centre_polygon.longitude);
    const addressBranch: IBranch = zoneAddressInfo?.branch;
    if ((addressBranch && (addressBranch.is_busy || !addressBranch.working_now)) || !addressBranch) {
      modalStore.openErrorModal({ message: localeStore.t('cc_err_delivery_is_unavailable') });
      return;
    }

    this.selectedZone = option;
    this.selectedAddress = undefined;
    this.changeDeliveryType(DELIVERY_TYPE.DELIVERY);
    const zoneBranch = branchStore.branches.find((branch) => branch.id === option?.data.branch.id);

    if (zoneBranch) {
      this.selectBranch(zoneBranch || defaultBranch);
      this.selectLocation({
        area_id: option.data.id,
        address: '',
        lat: option?.data.centre_polygon.latitude,
        lng: option?.data.centre_polygon.longitude,
      });
    }
  };

  // CLEAR
  @action clear = () => {
    // this.terminate();
    this.dependencies = defaultCheckoutDependencies;
    this.calculator = defaultCalculator;
    this.params = defaultCheckoutParams();
    this.isDraft = false;
    this.loading = false;
    this.validatorErrors = {};
    this.shouldCatchCalculationErrors = false;
    this.cashbackError = null;
    this.selectedAddress = undefined;
    this.addresses = [];
    this.selectedZone = undefined;
    this.isZoneAddress = false;
  };

  @action refundPrice = (): number => {
    const { calculator, dependencies } = this;
    // if order is at a discount (there is a discounted_total), then we calculate from it, otherwise from total price
    return (dependencies.order?.discounted_total || dependencies.order?.total || 0) - (calculator.discounted_total || calculator.total);
  };

  @action onlinePaymentNewTotalMaxValid = (): boolean => {
    return this.dependencies.order?.payment_type === PAYMENT_TYPES.ONLINE_PAYMENT && this.refundPrice() < 0;
  };

  @computed get cashbackUnavailable() {
    return (
      this.cashbackError &&
      [CashbackErrorCode.Unavailable, CashbackErrorCode.UnavailableForBranch, CashbackErrorCode.UnavailableForOrderType].includes(
        this.cashbackError
      ) &&
      !this.calculator.cashback.amount
    );
  }

  @computed get cashbackChanged() {
    return this.cashbackError && this.cashbackError === CashbackErrorCode.ChangedConditions;
  }
}
